import { aws, FRONTEND_URL, TOKEN_EXPIRY } from "../../../config/index";
import { TFunction } from "i18next";
import { IAttachment } from "../../../Modules/Attachment/interfaces/attcahment.interface";
import { GetAllUsersParams, IUser, RolesEnum, StatusEnum } from "../../../Modules/Auth/interfaces/auth.interface";
import User from "../../../Modules/Auth/models/auth.model";
import Permission from "../../../Modules/Permission/models/Permission.model";
import mongoose, { Types } from "mongoose";
import { Service, Container } from "typedi";
import { HttpException } from "../../../utils/exceptions/httpException";
import { status } from "../../../utils/helpers/api.responses";
import { createJWT, parseDurationToMs, sanitiseString } from "../../../utils/helpers/utilities.services";
import { resetPasswordEmail, sendDeactivationEmail, sendReactivationEmail } from "../../../utils/mail/mailer";
import { AttachmentService } from "../../../Modules/Attachment/services/attachment.service";
import bcrypt from 'bcryptjs'
import Role from "../../../Modules/Role/models/role.model";
import Labour from "../../../Modules/Labour/models/labour.model";
import crypto from 'crypto'
import { LabourService } from "../../../Modules/Labour/services/labour.service";
import Attachment from "../../../Modules/Attachment/models/attachment.model";
import { NotificationService } from "../../../Modules/Notification/services/notification.service";
import statusesEnums from '../../Labour/constant/status.json'
import Availability from "Modules/availabilityManagement/models/availability.model";


@Service()
export class UserManagementService {

    public attachmentService = Container.get(AttachmentService)
    public labourProfileService = Container.get(LabourService)

    public notificationService = Container.get(NotificationService)

    public async grantPermission(userId: string, permissionIds: string[], grantedBy: string, t: TFunction) {
        try {
            const user = await User.findOne({ _id: userId, isDeleted: false });
            const permission = await Permission.find({ _id: { $in: permissionIds } });
            if (!user || permission.length !== permissionIds.length) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('User.user') }));
            }

            // Check if any permissionIds exist in restrictedPermissions
            const restrictedIds = user.restrictedPermissions.map(p => p.permissionId.toString());
            const conflict = permissionIds.filter(id => restrictedIds.includes(id));
            if (conflict.length > 0) {
                throw new HttpException(status.BadRequest, t('userPermission.remove_from_restricted_first'));
            }

            const existingId = user.permissions.map(p => p.permissionId.toString());
            const newPermisssion = permissionIds.filter(id => !existingId.includes(id));

            newPermisssion.forEach(id => {
                user.permissions.push({ permissionId: new mongoose.Types.ObjectId(id), grantedBy: new mongoose.Types.ObjectId(grantedBy) });
            });
            await user.save();
            return {
                granted: newPermisssion,
                total: user.permissions.length
            };
        } catch (error) {

            if (error instanceof HttpException) throw error;
            throw new HttpException(status.InternalServerError, t('General.error'));
        }
    }


    public async restricPermission(userId: string, permissionIds: string[], grantedBy: string, t: TFunction) {
        try {
            const user = await User.findOne({ _id: userId, isDeleted: false })
            const permission = await Permission.find({ _id: { $in: permissionIds } });
            if (!user || permission.length !== permissionIds.length) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('User.user') }));
            }

            // Check if any permissionIds exist in granted permissions
            const grantedIds = user.permissions.map(p => p.permissionId.toString());
            const conflict = permissionIds.filter(id => grantedIds.includes(id));
            if (conflict.length > 0) {
                throw new HttpException(status.BadRequest, t('userPermission.revoke_from_granted_first'));
            }

            const existingId = user.restrictedPermissions.map(p => p.permissionId.toString());
            const newPermission = permissionIds
                .map(id => id.toString())
                .filter(id => !existingId.includes(id));

            newPermission.forEach(id => {
                user.restrictedPermissions.push({ permissionId: new mongoose.Types.ObjectId(id), grantedBy: new mongoose.Types.ObjectId(grantedBy) });
            });
            await user.save();
            return {
                granted: newPermission,
                total: user.restrictedPermissions.length
            };
        } catch (error) {

            if (error instanceof HttpException) throw error;
            throw new HttpException(status.InternalServerError, t('General.error'));
        }
    }




    public async revokPermission(userId: string, permissionIds: string[], grantedBy: string, t: TFunction, isRestricted: boolean) {
        try {
            const user = await User.findOne({ _id: userId, isDeleted: false });
            if (!user) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('User.user') }));
            }

            const targetArray = isRestricted ? user.restrictedPermissions : user.permissions;
            const existingPermissionIds = targetArray.map(p => p.permissionId.toString());
            const toBeRemoved = permissionIds.filter(id => existingPermissionIds.includes(id));

            if (toBeRemoved.length === 0) {
                throw new HttpException(status.BadRequest, isRestricted ? t('userPermission.none_to_revoke_restricted') : t('userPermission.none_to_revoke'));
            }

            // Filter out revoked permissions from target array
            if (isRestricted) {
                user.restrictedPermissions = user.restrictedPermissions.filter(perm => !toBeRemoved.includes(perm.permissionId.toString()));
            } else {
                user.permissions = user.permissions.filter(perm => !toBeRemoved.includes(perm.permissionId.toString()));
            }

            await user.save();

            return {
                revoked: toBeRemoved.length,
                remaining: isRestricted ? user.restrictedPermissions.length : user.permissions.length
            };
        } catch (error) {

            if (error instanceof HttpException) throw error;
            throw new HttpException(status.InternalServerError, t('General.error'));
        }
    }
    public async resendInvitation(email: string, requesterId: string, t: TFunction): Promise<void> {
        const session = await mongoose.startSession();
        try {
            session.startTransaction();

            const requester = await User.findById(requesterId)
                .populate('roleId')
                .session(session)
                .lean();
            if (!requester) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('User.user') }));
            }
            const requesterRole = (requester.roleId as any).name.toLowerCase();
            if (![RolesEnum.ADMIN.toLowerCase(), RolesEnum.STAFF.toLowerCase()].includes(requesterRole)) {
                throw new HttpException(status.Forbidden, t('Role.permission_denied'));
            }

            const user = await User.findOne({ email, isDeleted: false })
                .populate('profileImage')
                .session(session)
                .lean();
            if (!user) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('User.email') }));
            }

            if (user.status.toLowerCase() !== StatusEnum.WAITING_TO_ACCEPT.toLowerCase()) {
                throw new HttpException(status.BadRequest, t('User.notWaitingToAccept'));
            }

            const resetTokenResult = createJWT(user, parseDurationToMs('3d'));
            const resetToken = typeof resetTokenResult === 'string' ? resetTokenResult : resetTokenResult.accessToken;
            const resetTokenExpiry = new Date(Date.now() + parseDurationToMs('3d'));

            await User.updateOne(
                { _id: user._id },
                {
                    $set: {
                        forgotPassword: resetToken,
                        forgotpasswordTokenExpiry: resetTokenExpiry,
                        invitedAt: new Date(),
                    },
                },
                { session }
            );

            const fullName = `${user.firstName} ${user.lastName || ''}`.trim();
            const resetLink = `${process.env.FRONTEND_URL}/auth/reset-password/change?token=${resetToken}`;
            await resetPasswordEmail(user.email, resetLink, fullName);

            await session.commitTransaction();
        } catch (error) {
            await session.abortTransaction();
            if (error instanceof HttpException) throw error;
            throw new HttpException(status.InternalServerError, t('General.errorSendingInvitation'));
        } finally {
            session.endSession();
        }
    }



    private generateRandomPassword(length: number = 12): string {
        return crypto.randomBytes(length).toString("hex").slice(0, length);
    }

    public async createUser(data: Partial<IUser>, createdBy: string, t: TFunction, type: string) {
        const session = await mongoose.startSession();
        session.startTransaction();
        try {
            if (!data.email || !data.firstName || !data.lastName || !type) {
                throw new HttpException(status.BadRequest, t('General.required', { field: 'email, firstName, lastName, role' }));
            }
            const existingUser = await User.findOne({ email: data.email })
                .populate('roleId')
                .session(session)
                .lean();

            const role = await Role.findOne({ name: { $regex: new RegExp(`^${type}$`, 'i') } })
                .session(session)
                .lean();

            const currentUser = await User.findById(createdBy)
                .session(session)
                .lean();

            if (!role) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('Role.role') }));
            }
            if (!currentUser) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('User.user') }));
            }

            const currentRole = await Role.findById(currentUser.roleId).session(session);
            if (
                currentRole.name.toLowerCase() === RolesEnum.STAFF.toLowerCase() &&
                role.name.toLowerCase() !== RolesEnum.LABOUR.toLowerCase()
            ) {
                throw new HttpException(status.Forbidden, t('Role.permission_denied'));
            }

            const isLabour = role.name.toLowerCase() === RolesEnum.LABOUR.toLowerCase();

            if (existingUser?.isDeleted === true) {
                const resetTokenResult = createJWT(existingUser, parseDurationToMs(TOKEN_EXPIRY));
                const resetToken = typeof resetTokenResult === 'string' ? resetTokenResult : resetTokenResult.accessToken;
                const resetTokenExpiry = new Date(Date.now() + parseDurationToMs(TOKEN_EXPIRY));

                await User.updateOne(
                    { email: data.email, isDeleted: true },
                    {
                        $set: {
                            isDeleted: false,
                            deletedAt: null,
                            createdAt: Date.now(),
                            passwordUpdatedAt: null,
                            forgotPassword: resetToken,
                            forgotpasswordTokenExpiry: resetTokenExpiry,
                            isFirstTimeResetPassword: true,
                            isVerifiedOtp: true,
                            isVerifyOtpAt: Date.now(),
                            createdBy: createdBy,
                            firstName: data.firstName,
                            lastName: data.lastName,
                            profileImage: data.profileImage,
                            lockUntil: false,
                            status: StatusEnum.WAITING_TO_ACCEPT,
                            invitedAt: Date.now(),
                            // inviteStatus: InviteStatusEnum.WAITING_TO_ACCEPT,
                        },
                        $unset: {
                            token: '',
                            tokenExpiry: '',
                            refreshToken: '',
                            refreshTokenExpiry: '',
                            sessionId: '',
                        },
                    },
                    { session }
                );

                const reactivatedUser = await User.findOne({ email: data.email })
                    .select('_id email firstName lastName roleId status isDeleted joiningDate accountSetting.userName')
                    .populate<{ profileImage: IAttachment }>('profileImage')
                    .session(session)
                    .lean();

                const resetLink = `${FRONTEND_URL}/auth/reset-password/change?token=${resetToken}`;
                const fullName = `${data.firstName} ${data.lastName ? data.firstName : ''}`;
                await resetPasswordEmail(existingUser.email, resetLink, fullName);

                if (data.profileImage) {
                    await this.attachmentService.updateIsTemporaryFalse(data.profileImage.toString(), t, session);
                }

                if (isLabour) {
                    const labourExists = await Labour.findOne({ userId: reactivatedUser._id, isDeleted: true }).session(session);
                    if (!labourExists) {
                        await Labour.create(
                            [{
                                userId: reactivatedUser._id,
                                // verification_status: VerificationStatusEnum.PENDING,
                                profileCompletionStatus: statusesEnums.profileCompletionStatuses.PENDING,
                                draft: true,
                            }],
                            { session }
                        );
                    } else if (labourExists.isDeleted) {
                        labourExists.isDeleted = false;
                        labourExists.deletedAt = null;
                        await labourExists.save({ session });
                        const attachmentIds = [
                            ...(labourExists.certificates?.map(c => c.file) || []),
                            ...(labourExists.identification?.map(i => i.file) || []),
                        ];
                        if (attachmentIds.length > 0) {
                            await Attachment.updateMany(
                                { _id: { $in: attachmentIds } },
                                { $set: { isDeleted: false, deletedAt: null } },
                                { session }
                            );
                        }
                    }
                }

                await session.commitTransaction();
                return {
                    ...reactivatedUser,
                    profileImage: reactivatedUser.profileImage
                        ? {
                            _id: reactivatedUser.profileImage._id,
                            filename: reactivatedUser.profileImage.filename,
                            url: `${aws.S3_BASE_URL}/${reactivatedUser.profileImage.key}`,
                            fileSize: reactivatedUser.profileImage.fileSize
                        }
                        : null,
                };
            } else if (existingUser) {
                if (existingUser.status?.toLowerCase() === StatusEnum.DEACTIVATED.toLowerCase()) {
                    throw new HttpException(status.ResourceExist, t('User.reactiveUser'));
                }
                throw new HttpException(status.ResourceExist, t('General.already_exist', { field: t('User.email') }));
            }

            const randomPassword = this.generateRandomPassword();
            const hashedPassword = await bcrypt.hash(randomPassword, 10);

            const newUser = await User.create(
                [{
                    email: data.email,
                    firstName: data.firstName,
                    lastName: data.lastName,
                    roleId: role._id,
                    accountSetting: { passwordHash: hashedPassword },
                    // inviteStatus: InviteStatusEnum.WAITING_TO_ACCEPT,
                    invitedAt: Date.now(),
                    joiningDate: Date.now(),
                    createdAt: Date.now(),
                    createdBy: createdBy.toString(),
                    profileImage: data.profileImage,
                    status: StatusEnum.WAITING_TO_ACCEPT,
                }],
                { session }
            );

            const resetTokenResult = createJWT(newUser[0], parseDurationToMs(TOKEN_EXPIRY));
            const resetToken = typeof resetTokenResult === 'string' ? resetTokenResult : resetTokenResult.accessToken;
            const resetTokenExpiry = new Date(Date.now() + parseDurationToMs(TOKEN_EXPIRY));

            await User.updateOne(
                { _id: newUser[0]._id },
                {
                    $set: {
                        forgotPassword: resetToken,
                        forgotpasswordTokenExpiry: resetTokenExpiry,
                        isFirstTimeResetPassword: true,
                        isVerifiedOtp: true,
                        isVerifyOtpAt: new Date(),
                    },
                },
                { session }
            );

            const userData = await User.findById(newUser[0]._id)
                .select('_id email firstName lastName roleId accountSetting.userName')
                .populate<{ profileImage: IAttachment }>('profileImage')
                .session(session)
                .lean();

            const fullName = userData.firstName + " " + userData.lastName
            console.log("fullName", fullName)
            const resetLink = `${FRONTEND_URL}/auth/reset-password/change?token=${resetToken}`;
            await resetPasswordEmail(newUser[0].email, resetLink, fullName);

            if (userData.profileImage?._id) {
                const profileImageId = userData.profileImage._id.toString();
                await this.attachmentService.updateIsTemporaryFalse(profileImageId, t, session);
            }


            if (isLabour) {
                await Labour.create(
                    [{
                        userId: newUser[0]._id,
                        // verification_status: VerificationStatusEnum.PENDING,
                        profileCompletionStatus: statusesEnums.profileCompletionStatuses.PENDING,
                        draft: true,
                    }],
                    { session }
                );
            }

            await session.commitTransaction();
            return {
                ...userData,
                profileImage: userData.profileImage
                    ? {
                        _id: userData.profileImage._id,
                        filename: userData.profileImage.filename,
                        url: `${aws.S3_BASE_URL}/${userData.profileImage.key}`,
                        fileSize: userData.profileImage.fileSize
                    }
                    : null,
            };
        } catch (error) {
            console.log(error)
            await session.abortTransaction();

            if (error instanceof HttpException) throw error;
            throw new HttpException(status.InternalServerError, t('General.errorCreating', { field: t('User.user') }));
        } finally {
            session.endSession();
        }

    }

    public async getAllUsers(params: GetAllUsersParams): Promise<any> {
        const { createdBy, page, limit, search, roleFilter, sortBy, sortOrder, filters, t } = params;

        try {
            const user = await User.findById(createdBy).lean();
            if (!user) throw new HttpException(status.NotFound, t('General.not_found', { field: t('User.user') }));

            const role = await Role.findById(user.roleId).lean();
            if (!role) throw new HttpException(status.NotFound, t('General.not_found', { field: t('Role.role') }));

            // if (role.name.toLowerCase() === RolesEnum.STAFF.toLowerCase() && roleFilter.toLowerCase() === RolesEnum.STAFF.toLowerCase()) {
            //     throw new HttpException(status.Unauthorized, t('Role.permission_denied'));
            // }

            const skip = (page - 1) * limit;
            const nameRegex = new RegExp(sanitiseString(search.trim()), 'i');
            const emailRegex = new RegExp(sanitiseString(search.trim()), 'i');

            const baseFilter: any = {
                isDeleted: false,
                $or: [
                    { firstName: nameRegex },
                    { lastName: nameRegex },
                    { email: emailRegex },
                    {
                        $expr: {
                            $regexMatch: {
                                input: { $concat: ['$firstName', ' ', '$lastName'] },
                                regex: nameRegex,
                            },
                        },
                    },
                    { 'accountSetting.userName': nameRegex },
                ],
            };

            if (roleFilter) {
                if (Types.ObjectId.isValid(roleFilter)) {
                    baseFilter.roleId = new Types.ObjectId(roleFilter);
                } else if (roleFilter.toLowerCase() === 'adminorstaff') {
                    const roles = await Role.find({
                        name: { $in: [RolesEnum.ADMIN, RolesEnum.STAFF] },
                    }).select('_id').lean();
                    baseFilter.roleId = { $in: roles.map((r) => r._id) };
                } else {
                    const roleRegex = new RegExp(sanitiseString(roleFilter.trim()), 'i');
                    const roles = await Role.find({ name: roleRegex }).select('_id').lean();
                    if (roles.length === 0) {
                        return { users: [], total: 0, page, totalPage: 0 };
                    }
                    baseFilter.roleId = { $in: roles.map((r) => r._id) };
                }
            }
            for (const [key, value] of Object.entries(filters)) {
                if (
                    !['page', 'limit', 'search', 'role', 'sortBy', 'sortOrder'].includes(key) &&
                    typeof value === 'string' &&
                    value.trim()
                ) {
                    baseFilter[key] = new RegExp(sanitiseString(value), 'i');
                }
            }

            const pipeline: any[] = [
                { $match: baseFilter },
                {
                    $lookup: {
                        from: 'roles',
                        localField: 'roleId',
                        foreignField: '_id',
                        as: 'role',
                    },
                },
                {
                    $lookup: {
                        from: 'attachments',
                        localField: 'profileImage',
                        foreignField: '_id',
                        as: 'profileImageData',
                    },
                },
                {
                    $unwind: {
                        path: '$profileImageData',
                        preserveNullAndEmptyArrays: true,
                    },
                },
                { $unwind: '$role' },
                { $match: { 'role.name': { $nin: [RolesEnum.ADMIN] } } }, // Exclude Admin unless explicitly allowed
            ];

            // Remove Admin exclusion if roleFilter is 'adminorstaff' or explicitly includes Admin
            if (roleFilter && (roleFilter.toLowerCase() === 'adminorstaff' || roleFilter.toLowerCase() === RolesEnum.ADMIN.toLowerCase())) {
                pipeline.splice(pipeline.findIndex((p) => '$match' in p && p.$match['role.name']?.$nin?.includes(RolesEnum.ADMIN)), 1);
            }

            pipeline.push(
                {
                    $addFields: {
                        roleName: '$role.name',
                        profileImage: {
                            $cond: {
                                if: { $ne: ['$profileImageData', null] },
                                then: {
                                    _id: { $toString: '$profileImageData._id' },
                                    filename: '$profileImageData.filename',
                                    url: { $concat: [aws.S3_BASE_URL, '/', '$profileImageData.key'] },
                                    fileSize: '$profileImageData.fileSize'
                                },
                                else: null,
                            },
                        },
                    },
                },
                {
                    $sort: {
                        ...(sortBy ? { [sortBy]: sortOrder === 'desc' ? -1 : 1 } : { createdAt: -1 }),
                    },
                },
                { $skip: skip },
                { $limit: limit },
                {
                    $project: {
                        _id: 1,
                        email: 1,
                        firstName: 1,
                        lastName: 1,
                        roleName: 1,
                        profileImage: 1,
                        status: 1,
                        fullName: 1,
                    },
                }
            );

            const countPipeline = [
                ...pipeline.slice(0, pipeline.findIndex((p) => '$sort' in p || '$skip' in p || '$limit' in p)),
                { $count: 'total' },
            ];

            const [users, totalResult] = await Promise.all([
                User.aggregate(pipeline),
                User.aggregate(countPipeline),
            ]);

            const total = totalResult[0]?.total ?? 0;

            const enrichedUsers = await Promise.all(
                users.map(async (user) => {
                    if (user.roleName.toLowerCase() === RolesEnum.LABOUR.toLowerCase()) {
                        const labour = await Labour.findOne({ userId: user._id })
                            .select(
                                '_id availability_status occupation sitesInductedOn tickets employee_id skills certificates identification profileCompletionStatus experienceSummary profileCompletionPercentage'
                            )
                            .populate<{ certificates: { name: string; file: IAttachment }[], identification: { type: string; file: IAttachment }[] }>(
                                'certificates.file identification.file'
                            )
                            .lean();

                        if (labour) {
                            user.labourData = {
                                _id: labour._id,
                                employee_id: labour.employee_id,
                                skills: labour.skills || [],
                                experienceSummary: labour.experienceSummary,
                                occupation: labour.occupation,
                                sitesInductedOn: labour.sitesInductedOn,
                                tickets: Array.isArray(labour.tickets)
                                    ? labour.tickets.map((ticketCode) => statusesEnums.tickets[ticketCode] ?? ticketCode)
                                    : [],
                                certificates: Array.isArray(labour.certificates)
                                    ? labour.certificates.map((cert) => ({
                                        name: cert.name,
                                        file: cert.file
                                            ? {
                                                _id: cert.file._id,
                                                filename: cert.file.filename,
                                                url: `${aws.S3_BASE_URL}/${cert.file.key}`,
                                                thumbnailUrl: `${aws.S3_BASE_URL}/${cert.file.thumbnailKey}`,
                                            }
                                            : null,
                                    }))
                                    : [],
                                identification: Array.isArray(labour.identification)
                                    ? labour.identification.map((id) => ({
                                        type: id.type,
                                        file: id.file
                                            ? {
                                                _id: id.file._id,
                                                filename: id.file.filename,
                                                url: `${aws.S3_BASE_URL}/${id.file.key}`,
                                                thumbnailUrl: `${aws.S3_BASE_URL}/${id.file.thumbnailKey}`,
                                            }
                                            : null,
                                    }))
                                    : [],
                                availability_status: labour.availability_status,
                                profileCompletionStatus: labour.profileCompletionStatus,
                                profileCompletionPercentage: labour.profileCompletionPercentage,
                            };
                        }
                    }
                    return user;
                })
            );

            return {
                users: enrichedUsers,
                total,
                page,
                totalPage: Math.ceil(total / limit),
            };
        } catch (error) {
            console.log(error);
            if (error instanceof HttpException) throw error;
            throw new HttpException(status.InternalServerError, t('General.errorFetching', { field: t('User.user') }));
        }
    }

    public async getById(userId: string, t: TFunction): Promise<any> {
        try {
            if (!userId) {
                throw new HttpException(
                    status.BadRequest, t('General.not_found', { field: t('User.user') })
                );
            }


            const userData = await User.findOne({ _id: userId, isDeleted: false })
                .select([
                    '_id',
                    'email',
                    'firstName',
                    'lastName',
                    'fullName',
                    'phone',
                    'gender',
                    'date_of_birth',
                    'joiningDate',
                    'status',
                    // 'inviteStatus',
                    'accountSetting.userName',
                    'accountSetting.lastLogin',
                    'roleId',
                    'profileImage',
                    'createdBy',
                    'countryCode'
                ])
                .populate<{ profileImage: IAttachment }>('profileImage').lean()
            if (!userData) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('User.user') }))
            }
            const roleData = await Role.findById(userData.roleId)
            let labourData = null;
            if (roleData?.name.toLowerCase() === RolesEnum.LABOUR.toLowerCase()) {
                const labour = await Labour.findOne({ userId })
                    .select('_id ratingCount averageRating profileCompletionPercentage tickets occupation sitesInductedOn availability_status employee_id skills certificates identification profileCompletionStatus experienceSummary')
                    .populate<{ certificates: { name: string; file: IAttachment }[], identification: { type: string; file: IAttachment }[] }>('certificates.file identification.file')
                    .lean();

                if (labour) {
                    labourData = {
                        _id: labour._id,
                        employee_id: labour.employee_id,
                        skills: labour.skills,
                        sitesInductedOn: labour.sitesInductedOn,
                        ratingCount: labour.ratingCount ?? null,
                        averageRating: labour.averageRating ?? null,
                        certificates: Array.isArray(labour.certificates)
                            ? labour.certificates
                                .filter(cert => cert.file)
                                .map(cert => ({
                                    name: cert.name,
                                    file: {
                                        _id: cert.file._id,
                                        filename: cert.file.filename,
                                        fileSize: cert.file.fileSize,
                                        url: `${aws.S3_BASE_URL}/${cert.file.key}`,
                                        thumbnailUrl: `${aws.S3_BASE_URL}/${cert.file.thumbnailKey}`
                                    }
                                }))
                            : [],

                        tickets: Array.isArray(labour.tickets)
                            ? labour.tickets.map(ticketCode => statusesEnums.tickets[ticketCode] ?? ticketCode)
                            : [],

                        occupation: labour.occupation,
                        identification: Array.isArray(labour.identification)
                            ? labour.identification
                                .filter(id => id.file)
                                .map(id => ({
                                    type: id.type,
                                    file: {
                                        _id: id.file._id,
                                        fileSize: id.file.fileSize,
                                        filename: id.file.filename,
                                        url: `${aws.S3_BASE_URL}/${id.file.key}`,
                                        thumbnailUrl: `${aws.S3_BASE_URL}/${id.file.thumbnailKey}`
                                    }
                                }))
                            : [],

                        availability_status: labour.availability_status,
                        profileCompletionStatus: labour.profileCompletionStatus,
                        experienceSummary: labour.experienceSummary,
                        profileCompletionPercentage: labour.profileCompletionPercentage
                    };
                }
            }



            return {
                ...userData,
                profileImage: userData.profileImage
                    ? {
                        _id: userData.profileImage._id.toString(),
                        filename: userData.profileImage.filename,
                        url: `${aws.S3_BASE_URL}/${userData.profileImage.key}`,
                        fileSize: userData.profileImage.fileSize
                    }
                    : null,
                roleName: roleData.name,
                labour: labourData
            }
        } catch (error) {
            console.log(error)
            if (error instanceof HttpException) throw error
            throw new HttpException(status.InternalServerError, t('General.errorFetching', { field: t('User.user') }))
        }
    }



    public async deleteUser(userIds: string[], createdBy: string, roleId: string, t: TFunction) {
        const session = await mongoose.startSession();

        try {
            session.startTransaction();

            const requesterRole = await Role.findById(roleId).session(session);
            const isAdmin = [RolesEnum.ADMIN.toLowerCase()].includes(requesterRole?.name?.toLowerCase());

            const deletedCount = 0;

            for (const userId of userIds) {
                const user = await User.findOne({
                    _id: userId,
                    ...(isAdmin ? {} : { createdBy }),
                    isDeleted: false,
                })
                    .populate('roleId')
                    .session(session);

                if (!user) {
                    throw new HttpException(status.NotFound, t('General.not_found', { field: t('User.user') }));
                }

                const targetRole = (user.roleId as any).name;

                if (targetRole?.toLowerCase() === RolesEnum.STAFF.toLowerCase() && !isAdmin) {
                    throw new HttpException(status.Forbidden, t('Role.permission_denied'));
                }

                if (targetRole?.toLowerCase() === RolesEnum.LABOUR.toLowerCase()) {
                    const activeAvailability = await Availability.findOne({
                        userId,
                        isDeleted: false,
                    }).session(session);

                    if (activeAvailability) {
                        throw new HttpException(
                            status.BadRequest,
                            t('Labour.cannotDeleteActiveAvailability', { field: user.firstName })
                        );
                    }


                    const labourDeleteResult = await Labour.findOneAndUpdate(
                        { userId },
                        { $set: { isDeleted: true, deletedAt: new Date() } },
                        { session, new: true }
                    );

                    if (!labourDeleteResult) {
                        throw new HttpException(status.NotFound, t('General.not_found', { field: t('Labour.labour') }));
                    }

                    const attachmentIds = [
                        ...(labourDeleteResult.certificates?.map(c => c.file) || []),
                        ...(labourDeleteResult.identification?.map(i => i.file) || []),
                    ];

                    if (attachmentIds.length > 0) {
                        await Attachment.updateMany(
                            { _id: { $in: attachmentIds } },
                            { $set: { isDeleted: true, deletedAt: new Date() } },
                            { session }
                        );
                    }
                }

                const result = await User.updateOne(
                    { _id: userId, ...(isAdmin ? {} : { createdBy }), isDeleted: false },
                    {
                        $set: {
                            isDeleted: true,
                            deletedAt: new Date(),
                            token: null,
                            tokenExpiry: null,
                            forgotPassword: null,
                            forgotpasswordTokenExpiry: null,
                            status: StatusEnum.DEACTIVATED,
                            sessionId: null
                        },
                    },
                    { session }
                );

                if (result.modifiedCount === 0) {
                    throw new HttpException(status.NotFound, t('General.not_found', { field: t('User.user') }));
                }

                await session.commitTransaction();
                if (
                    targetRole.toLowerCase() === RolesEnum.LABOUR.toLowerCase() &&
                    requesterRole.name.toLowerCase() === RolesEnum.STAFF.toLowerCase()
                ) {
                    const adminRole = await Role.findOne({ name: { $regex: new RegExp(`^${RolesEnum.ADMIN}$`, 'i') } }).session(session);
                    const admins = await User.find({
                        roleId: { $in: [adminRole?._id].filter(Boolean) },
                        isDeleted: false,
                    })
                        .select('_id')
                        .session(session);

                    await this.notificationService.sendNotification(
                        'labourManagement',
                        'labourDeletedByStaff',
                        {
                            from: new mongoose.Types.ObjectId(createdBy),
                            to: admins.map(id => new mongoose.Types.ObjectId(id._id)),
                            entityData: {
                                firstName: user.firstName
                            }
                        },
                        t,
                        // session
                    );
                }
            }

            return { deletedCount: userIds.length };

        } catch (error) {
            await session.abortTransaction();
            if (error instanceof HttpException) throw error;
            throw new HttpException(status.InternalServerError, t('General.errorDeleting', { field: t('User.user') }));
        } finally {
            session.endSession();
        }
    }


    public async deleteMultiUser(
        userIds: string[],
        createdBy: string,
        roleId: string,
        t: TFunction
    ): Promise<{ deletedCount: number }> {
        const session = await mongoose.startSession();

        try {
            session.startTransaction();

            const requesterRole = await Role.findById(roleId).session(session);
            const isAdmin = requesterRole?.name?.toLowerCase() === RolesEnum.ADMIN.toLowerCase();

            const users = await User.find({
                _id: { $in: userIds },
                isDeleted: false,
                ...(isAdmin ? {} : { createdBy }),
            })
                .populate('roleId')
                .session(session);

            if (!users.length) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('User.user') }));
            }

            const labourUserIds: string[] = [];
            const labourAttachmentIds: string[] = [];
            const toNotifyAdmins: mongoose.Types.ObjectId[] = [];

            for (const user of users) {
                const roleName = (user.roleId as any).name?.toLowerCase();

                if (roleName === RolesEnum.STAFF.toLowerCase() && !isAdmin) {
                    throw new HttpException(status.Forbidden, t('Role.permission_denied'));
                }

                if (roleName === RolesEnum.LABOUR.toLowerCase()) {
                    const labour = await Labour.findOneAndUpdate(
                        { userId: user._id },
                        { $set: { isDeleted: true, deletedAt: new Date() } },
                        { session, new: true }
                    );

                    if (labour) {
                        labourUserIds.push(user._id.toString());

                        labourAttachmentIds.push(
                            ...((labour.certificates || []).map(c => c.file.toString())),
                            ...((labour.identification || []).map(i => i.file.toString()))
                        );
                    }

                    if (requesterRole?.name?.toLowerCase() === RolesEnum.STAFF.toLowerCase()) {
                        const adminRole = await Role.findOne({
                            name: { $regex: new RegExp(`^${RolesEnum.ADMIN}$`, 'i') },
                        }).session(session);

                        const admins = await User.find({
                            roleId: adminRole?._id,
                            isDeleted: false,
                        })
                            .select('_id')
                            .session(session);

                        toNotifyAdmins.push(...admins.map((a) => new Types.ObjectId(a._id)));
                    }
                }
            }

            if (labourAttachmentIds.length > 0) {
                await Attachment.updateMany(
                    { _id: { $in: labourAttachmentIds } },
                    { $set: { isDeleted: true, deletedAt: new Date() } },
                    { session }
                );
            }

            await User.updateMany(
                {
                    _id: { $in: userIds },
                    isDeleted: false,
                    ...(isAdmin ? {} : { createdBy }),
                },
                {
                    $set: {
                        isDeleted: true,
                        deletedAt: new Date(),
                        token: null,
                        tokenExpiry: null,
                        forgotPassword: null,
                        forgotpasswordTokenExpiry: null,
                        status: StatusEnum.DEACTIVATED,
                        sessionId: null,
                    },
                },
                { session }
            );

            await session.commitTransaction();
            // Send notifications to admins if required
            if (toNotifyAdmins.length > 0) {
                const notifications = users
                    .filter(user => (user.roleId as any).name?.toLowerCase() === RolesEnum.LABOUR.toLowerCase())
                    .map(user =>
                        this.notificationService.sendNotification(
                            'labourManagement',
                            'labourDeletedByStaff',
                            {
                                from: new mongoose.Types.ObjectId(createdBy),
                                to: toNotifyAdmins,
                                entityData: { firstName: user.firstName },
                            },
                            t,
                            // session
                        )
                    );

                await Promise.all(notifications);
            }


            return { deletedCount: userIds.length };
        } catch (error) {
            await session.abortTransaction();
            if (error instanceof HttpException) throw error;
            throw new HttpException(
                status.InternalServerError,
                t('General.errorDeleting', { field: t('User.user') })
            );
        } finally {
            session.endSession();
        }
    }




    public async updateUserProfile(userId: string, currentUser: string, data: any, t: TFunction) {
        try {
            const user = await User.findById(userId).populate<{ profileImage: IAttachment }>('profileImage');
            if (!user) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('User.user') }));
            }
            const normalizedProfileImage = data.profileImage === '' || data.profileImage === null ? null : data.profileImage;

            if (normalizedProfileImage) {
                const attachments = await this.attachmentService.getAttachmentsByIds(data.profileImage, t);
                if (!attachments.length) {
                    throw new HttpException(status.BadRequest, t('General.not_found', { field: t('Upload.file') }));
                }
            }
            const isNewImage = normalizedProfileImage &&
                user.profileImage &&
                normalizedProfileImage.toString() !== user.profileImage._id?.toString();

            const [currentUserData, adminRole, staffRole] = await Promise.all([
                User.findById(currentUser).select('_id roleId').lean(),
                Role.findOne({ name: new RegExp(`^${RolesEnum.ADMIN}$`, 'i') }).lean(),
                Role.findOne({ name: new RegExp(`^${RolesEnum.STAFF}$`, 'i') }).lean()
            ]);
            const role = await Role.findById(currentUserData.roleId)
            const targetRole = await Role.findById(user.roleId)
            if (role.name.toLowerCase() === RolesEnum.STAFF.toLowerCase() && targetRole.name.toLowerCase() === RolesEnum.STAFF.toLowerCase()) {
                throw new HttpException(status.Forbidden, t('Role.permission_denied'))
            }
            if (role.name.toLowerCase() === RolesEnum.STAFF.toLowerCase() && targetRole.name.toLowerCase() === RolesEnum.ADMIN.toLowerCase()) {
                throw new HttpException(status.Forbidden, t('Role.permission_denied'))
            }
            const adminRoleId = adminRole?._id?.toString();
            const staffRoleId = staffRole?._id?.toString();
            const currentUserRoleId = currentUserData?.roleId?.toString();



            let newStatus: string | undefined;

            const normalizedInput = data.status?.trim().toLowerCase();
            let statusUpdated = false;
            if (normalizedInput) {
                const inputToEnum: Record<string, string> = {
                    active: StatusEnum.ACTIVE,
                    activated: StatusEnum.ACTIVE,
                    activate: StatusEnum.ACTIVE,
                    deactive: StatusEnum.DEACTIVATED,
                    deactivated: StatusEnum.DEACTIVATED,
                    deactivate: StatusEnum.DEACTIVATED,
                };
                newStatus = inputToEnum[normalizedInput];

                if (!newStatus) {
                    throw new HttpException(status.BadRequest, t('General.invalid', { field: t('status') }));
                }

                if (
                    newStatus.toLowerCase() === StatusEnum.DEACTIVATED.toLowerCase() &&
                    ![adminRoleId, staffRoleId].includes(currentUserRoleId)
                ) {
                    throw new HttpException(status.Forbidden, t('Role.permission_denied'));
                }
                const currentStatus = user.status?.trim().toLowerCase();
                if (currentStatus !== newStatus.toLowerCase()) {
                    user.status = newStatus;
                    statusUpdated = true;
                }

                user.status = newStatus;
            }



            Object.assign(user, {
                firstName: data.firstName ?? user.firstName,
                lastName: data.lastName ?? user.lastName,
                phone: data.phone ?? user.phone,
                gender: data.gender ?? user.gender,
                date_of_birth: data.date_of_birth ?? user.date_of_birth,
                profileImage: normalizedProfileImage === null ? null : normalizedProfileImage ?? user.profileImage,
                countryCode: data.countryCode ?? user.countryCode,
                // status: data.status ? statusMap[inputStatus] : user.status,

            });

            await user.save();

            if (statusUpdated) {
                if (newStatus.toLowerCase() === StatusEnum.DEACTIVATED.toLowerCase()) {
                    await sendDeactivationEmail(user.email, user.firstName + " " + user.lastName);
                } else if (newStatus.toLowerCase() === StatusEnum.ACTIVE.toLowerCase()) {
                    await sendReactivationEmail(user.email, user.firstName + " " + user.lastName);
                }
            }
            // if (newStatus.toLowerCase() === StatusEnum.DEACTIVATED.toLowerCase()) {
            //     await sendDeactivationEmail(user.email, `${user.firstName} ${user.lastName}`);
            // } else if (newStatus.toLowerCase() === StatusEnum.ACTIVE.toLowerCase()) {
            //     await sendReactivationEmail(user.email, `${user.firstName} ${user.lastName}`);
            // }
            if (isNewImage) {
                await this.attachmentService.updateIsTemporaryFalse(normalizedProfileImage.toString(), t);
            }


            const updatedUser = await User.findById(userId)
                .select('_id email firstName lastName countryCode phone gender date_of_birth profileImage createdBy roleId fullName')
                .populate<{ profileImage: IAttachment }>('profileImage')
                .lean();


            const updatedUserRoleId = updatedUser?.roleId?.toString();


            let notificationAction: string | null = null;
            let recipients: Types.ObjectId[] = [];

            if (userId === currentUser && currentUserRoleId === staffRoleId) {
                notificationAction = 'staffUpdatedProfile';
                const admins = await User.find({
                    roleId: adminRoleId,
                    status: { $in: [StatusEnum.ACTIVE, StatusEnum.WAITING_TO_ACCEPT] }
                }).select('_id').lean();

                recipients = admins.map(u => new mongoose.Types.ObjectId(u._id));

            } else if (currentUserRoleId === adminRoleId && updatedUserRoleId === staffRoleId) {
                notificationAction = 'adminUpdatedProfile';
                recipients = [new mongoose.Types.ObjectId(userId)];
            }

            if (notificationAction && recipients.length) {
                await this.notificationService.sendNotification(
                    'userManagement',
                    notificationAction,
                    {
                        from: new mongoose.Types.ObjectId(currentUser),
                        to: recipients,
                        entityData: updatedUser
                    },
                    t
                );
            }

            return {
                ...updatedUser,
                profileImage: updatedUser?.profileImage
                    ? {
                        _id: updatedUser.profileImage._id,
                        filename: updatedUser.profileImage.filename,
                        url: `${aws.S3_BASE_URL}/${updatedUser.profileImage.key}`,
                        fileSize: updatedUser.profileImage.fileSize
                    }
                    : null,
            };

        } catch (error) {
            if (error instanceof HttpException) throw error;
            throw new HttpException(status.InternalServerError, t('General.errorUpdating', { field: t('User.profile') }));
        }
    }




    public async changeStatus(userStatus: string, userId: string, t: TFunction) {
        const user = await User.findById(userId);
        if (!user) {
            throw new HttpException(status.NotFound, t('General.not_found', { field: t('User.user') }));
        }

        const normalizedStatus = userStatus.toLowerCase();

        let newStatus: string;
        if (normalizedStatus === StatusEnum.ACTIVE.toLowerCase()) {
            newStatus = StatusEnum.ACTIVE;
        } else if (normalizedStatus === StatusEnum.DEACTIVATED.toLowerCase()) {
            newStatus = StatusEnum.DEACTIVATED;
        } else {
            throw new HttpException(status.BadRequest, t('User.invalidStatus'));
        }

        if (user.status === newStatus) {
            throw new HttpException(
                status.BadRequest,
                newStatus === StatusEnum.DEACTIVATED
                    ? t('User.alreadyDeactivated')
                    : t('User.alreadyActive')
            );
        }

        user.status = newStatus;
        await user.save();

        if (newStatus === StatusEnum.DEACTIVATED) {
            await sendDeactivationEmail(user.email, `${user.firstName} ${user.lastName}`);
        } else if (newStatus === StatusEnum.ACTIVE) {
            await sendReactivationEmail(user.email, `${user.firstName} ${user.lastName}`);
        }

        return true;
    }

}